import React, { useState } from "react";
import { Box, Button, Collapse } from "@mui/material";
import { DateTime } from "luxon";
import { IconChevronRight, IconCopy, IconCheck } from "@tabler/icons-react";
import tokens from "../../theme/tokens";
import { formatScore } from "./candidateRow";
import { orderedEntries, fieldLabel, formatValue } from "./screeningFields";

const { color, font, radius, scoreColor } = tokens;

const CALL_STATUS_LABEL = {
  dial_no_answer: "No answer",
  dial_busy: "Line busy",
  went_to_voicemail: "Went to voicemail",
};

const CRITERION_LABEL = {
  communication: "Communication",
  relevance: "Relevance",
  experience: "Experience",
  role_fit: "Role fit",
  red_flags: "Red flags",
};

/** Same precedence the old ScreeningDetailDialog used to label an attempt. */
const outcomeOf = (r) => {
  if (r.interview_rating != null) {
    return { label: "Completed", bg: color.successBg, fg: color.success };
  }
  if (CALL_STATUS_LABEL[r.call_status]) {
    return { label: CALL_STATUS_LABEL[r.call_status], bg: color.dangerBg, fg: color.danger };
  }
  if (r.outcome === "followup_with_time" || r.outcome === "followup_no_time") {
    return { label: "Follow-up requested", bg: color.warningBg, fg: color.warning };
  }
  return { label: "Logged", bg: color.borderRow, fg: color.textSecondary };
};

const Row = ({ label, children }) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      gap: 2,
      py: 0.75,
      borderBottom: `1px solid ${color.borderRow}`,
    }}
  >
    <Box sx={{ font: `400 12px ${font.sans}`, color: color.textSecondary }}>{label}</Box>
    <Box sx={{ font: `500 12px ${font.mono}`, color: color.text, textAlign: "right" }}>
      {children}
    </Box>
  </Box>
);

const SubTitle = ({ children, action }) => (
  <Box
    sx={{
      mt: 2,
      mb: 0.75,
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 1,
      font: `600 10.5px ${font.sans}`,
      color: color.textMuted,
      letterSpacing: ".06em",
    }}
  >
    <Box component="span">{children}</Box>
    {action}
  </Box>
);

/**
 * Copies a block of text — a transcript is the one artefact here a recruiter reliably wants to
 * paste somewhere else, and selecting it by hand out of a 200px scroll box is miserable.
 *
 * navigator.clipboard is unavailable outside a secure context (plain-http staging, a LAN IP), so
 * this falls back to the old execCommand path rather than dying silently. If both fail the button
 * says so — a copy button that quietly does nothing is worse than no button.
 */
const CopyButton = ({ text, label = "Copy" }) => {
  const [state, setState] = useState("idle");

  const copy = async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        const ok = document.execCommand("copy");
        document.body.removeChild(ta);
        if (!ok) throw new Error("execCommand copy rejected");
      }
      setState("copied");
    } catch {
      setState("failed");
    }
    setTimeout(() => setState("idle"), 1800);
  };

  return (
    <Button
      size="small"
      onClick={copy}
      startIcon={
        state === "copied" ? <IconCheck size={13} stroke={2} /> : <IconCopy size={13} stroke={2} />
      }
      sx={{
        minWidth: 0,
        py: 0.125,
        px: 0.75,
        font: `500 10.5px ${font.sans}`,
        letterSpacing: 0,
        textTransform: "none",
        color: state === "failed" ? color.danger : color.textSecondary,
        "&:hover": { bgcolor: color.rowTint },
      }}
    >
      {state === "copied" ? "Copied" : state === "failed" ? "Copy failed" : label}
    </Button>
  );
};

/**
 * One screening call attempt, collapsed to a header row and expandable to the full
 * record: rating, per-criterion scores, summary, recording, transcript.
 *
 * Every attempt gets this treatment — a candidate is often called several times, and
 * the earlier attempts (why they didn't pick up, what the follow-up ask was) are the
 * ones a recruiter actually needs to read before calling again.
 */
const ScreeningAttempt = ({ result, index, total, isBest = false, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);

  const status = outcomeOf(result);
  const perCriterion = result.interview_rating_details?.per_criterion || {};
  // results[0] is the latest, so attempt numbers count down the list.
  const attemptNo = result.attempt_count ?? total - index;

  return (
    <Box
      sx={{
        border: `1px solid ${color.border}`,
        borderRadius: `${radius.fileRow}px`,
        mb: 1,
        overflow: "hidden",
      }}
    >
      <Box
        onClick={() => setOpen((o) => !o)}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 1.5,
          py: 1.25,
          cursor: "pointer",
          bgcolor: open ? color.rowTint : color.surface,
          "&:hover": { bgcolor: color.rowTint },
        }}
      >
        <Box
          sx={{
            display: "flex",
            transform: open ? "rotate(90deg)" : "none",
            transition: "transform 120ms ease",
          }}
        >
          <IconChevronRight size={14} stroke={2} color={color.textMuted} />
        </Box>

        <Box sx={{ font: `500 11px ${font.mono}`, color: color.textMuted }}>
          #{attemptNo}
        </Box>

        <Box
          component="span"
          sx={{
            font: `600 10px ${font.sans}`,
            textTransform: "uppercase",
            borderRadius: `${radius.chip}px`,
            px: 0.875,
            py: 0.375,
            bgcolor: status.bg,
            color: status.fg,
            whiteSpace: "nowrap",
          }}
        >
          {status.label}
        </Box>

        <Box sx={{ flex: 1 }} />

        {/* Marks which attempt the headline screen score came from, so a recruiter
            isn't left wondering why the chip says 8.6 when the last call was a no-answer. */}
        {isBest && (
          <Box
            component="span"
            sx={{
              font: `600 9.5px ${font.sans}`,
              textTransform: "uppercase",
              letterSpacing: ".04em",
              borderRadius: `${radius.chip}px`,
              px: 0.75,
              py: 0.25,
              bgcolor: color.indigoTint,
              color: color.indigo,
            }}
          >
            Best
          </Box>
        )}

        {result.interview_rating != null && (
          <Box sx={{ font: `600 12px ${font.mono}`, color: scoreColor(result.interview_rating) }}>
            {formatScore(result.interview_rating)}
          </Box>
        )}

        <Box sx={{ font: `400 10.5px ${font.mono}`, color: color.textMuted, whiteSpace: "nowrap" }}>
          {result.created_at
            ? DateTime.fromISO(result.created_at).toFormat("dd LLL, HH:mm")
            : "—"}
        </Box>
      </Box>

      <Collapse in={open} unmountOnExit>
        <Box sx={{ px: 1.5, pb: 1.75, pt: 0.5 }}>
          {result.call_duration != null && (
            <Row label="Call duration">{result.call_duration}s</Row>
          )}
          {result.call_recording_link && (
            <>
              <SubTitle>RECORDING</SubTitle>
              <Box
                component="audio"
                controls
                preload="auto"
                src={result.call_recording_link}
                sx={{ width: "100%", height: 34 }}
              />
            </>
          )}
          {result.callback_time && (
            <Row label="Callback requested">
              {DateTime.fromISO(result.callback_time).toFormat("dd LLL, HH:mm")}
            </Row>
          )}

          {Object.keys(perCriterion).length > 0 && (
            <>
              <SubTitle>PER-CRITERION</SubTitle>
              {Object.entries(perCriterion).map(([key, value]) => (
                <Row key={key} label={CRITERION_LABEL[key] || key}>
                  {typeof value === "number" ? formatScore(value) : String(value)}
                </Row>
              ))}
            </>
          )}

          {/* Every key the webhook sent, including ones this build has never heard of —
              the sender owns that schema and it grows. Unknown keys render humanized rather
              than being dropped. Values are raw, not currency-formatted: see screeningFields.js. */}
          {orderedEntries(result.screening_data).length > 0 && (
            <>
              <SubTitle>SCREENING DATA</SubTitle>
              {orderedEntries(result.screening_data).map(([key, value]) => (
                <Row key={key} label={fieldLabel(key)}>
                  {formatValue(value)}
                </Row>
              ))}
            </>
          )}

          {result.call_summary && (
            <>
              <SubTitle action={<CopyButton text={result.call_summary} />}>CALL SUMMARY</SubTitle>
              <Box
                sx={{
                  font: `400 12.5px/1.65 ${font.sans}`,
                  color: color.textSecondary,
                  whiteSpace: "pre-wrap",
                }}
              >
                {result.call_summary}
              </Box>
            </>
          )}

          {result.disqualification_reason && (
            <>
              <SubTitle>DISQUALIFICATION REASON</SubTitle>
              <Box sx={{ font: `400 12.5px/1.65 ${font.sans}`, color: color.danger }}>
                {result.disqualification_reason}
              </Box>
            </>
          )}

          

          {result.transcript && (
            <>
              <SubTitle action={<CopyButton text={result.transcript} />}>TRANSCRIPT</SubTitle>
              <Box
                sx={{
                  maxHeight: 200,
                  overflowY: "auto",
                  bgcolor: color.bg,
                  border: `1px solid ${color.border}`,
                  borderRadius: `${radius.chip}px`,
                  p: 1.25,
                  font: `400 11.5px/1.7 ${font.mono}`,
                  color: color.textSecondary,
                  whiteSpace: "pre-wrap",
                }}
              >
                {result.transcript}
              </Box>
            </>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

export default ScreeningAttempt;
